class GrowHomeCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) return;

    this.slides = Array.from(this.track.querySelectorAll('[data-carousel-slide]'));
    if (this.slides.length === 0) {
      this.slides = Array.from(this.track.children);
    }

    this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));

    this.handleScroll = this.handleScroll.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
    this.handleClickCapture = this.handleClickCapture.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleDotClick = this.handleDotClick.bind(this);

    this.track.addEventListener('scroll', this.handleScroll, { passive: true });
    this.track.addEventListener('pointerdown', this.handlePointerDown);
    this.addEventListener('click', this.handleClickCapture, true);

    for (const dot of this.dots) {
      dot.addEventListener('click', this.handleDotClick);
    }

    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.track);

    this.updateState();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.handleScroll);
    this.track?.removeEventListener('pointerdown', this.handlePointerDown);
    this.removeEventListener('click', this.handleClickCapture, true);

    for (const dot of this.dots || []) {
      dot.removeEventListener('click', this.handleDotClick);
    }

    this.resizeObserver?.disconnect();
    this.teardownDrag();
  }

  handleResize() {
    this.updateState();
  }

  handleScroll() {
    if (this.scrollFrame) return;

    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.updateState();
    });
  }

  handleDotClick(event) {
    const targetIndex = this.dots.indexOf(event.currentTarget);
    if (targetIndex < 0) return;

    this.scrollToIndex(targetIndex);
  }

  handlePointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0 || !this.isScrollable) return;

    this.dragStartX = event.clientX;
    this.dragStartScrollLeft = this.track.scrollLeft;
    this.dragMoved = false;
    this.pointerId = event.pointerId;

    this.setAttribute('dragging', '');
    this.track.setPointerCapture(event.pointerId);
    this.track.style.scrollSnapType = 'none';

    this.track.addEventListener('pointermove', this.handlePointerMove);
    this.track.addEventListener('pointerup', this.handlePointerUp, { once: true });
    this.track.addEventListener('pointercancel', this.handlePointerUp, { once: true });
  }

  handlePointerMove(event) {
    const delta = event.clientX - this.dragStartX;
    if (Math.abs(delta) > 6) {
      this.dragMoved = true;
    }

    this.track.scrollLeft = this.dragStartScrollLeft - delta;
  }

  handlePointerUp() {
    this.teardownDrag();

    if (!this.dragMoved) return;

    this.suppressClick = true;
    window.setTimeout(() => {
      this.suppressClick = false;
    }, 0);
    this.scrollToIndex(this.getActiveIndex());
  }

  handleClickCapture(event) {
    if (!this.suppressClick) return;

    const link = event.target.closest('a');
    if (!link || !this.contains(link)) return;

    event.preventDefault();
    event.stopPropagation();
  }

  teardownDrag() {
    if (!this.track) return;

    this.removeAttribute('dragging');
    this.track.style.scrollSnapType = '';

    if (this.pointerId !== undefined) {
      try {
        this.track.releasePointerCapture(this.pointerId);
      } catch (_error) {
      }
    }

    this.pointerId = undefined;
    this.track.removeEventListener('pointermove', this.handlePointerMove);
  }

  updateState() {
    this.isScrollable = this.track.scrollWidth - this.track.clientWidth > 1;
    this.toggleAttribute('is-scrollable', this.isScrollable);

    const activeIndex = this.getActiveIndex();
    for (const [index, dot] of this.dots.entries()) {
      dot.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
    }
  }

  getActiveIndex() {
    if (!this.slides?.length) return 0;

    const viewportCenter = this.track.scrollLeft + this.track.clientWidth / 2;
    let activeIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    for (const [index, slide] of this.slides.entries()) {
      const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
      const distance = Math.abs(slideCenter - viewportCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        activeIndex = index;
      }
    }

    return activeIndex;
  }

  scrollToIndex(index) {
    const slide = this.slides[index];
    if (!slide) return;

    this.track.scrollTo({
      left: slide.offsetLeft,
      behavior: 'smooth',
    });
  }
}

if (!customElements.get('grow-home-carousel')) {
  customElements.define('grow-home-carousel', GrowHomeCarousel);
}
